import { useTranslation } from 'react-i18next';
import { CircleHelp } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { isMac } from './model-input';

interface ModelControlsHelpProps {
  /** False where a sector's limits allow no sliding at all. */
  canPan?: boolean;
  /** False under camera limits, which are measured around the pivot. */
  canPivot?: boolean;
  className?: string;
}

/**
 * What the mouse and the keyboard do on a crag's model. Only where there is a pointer:
 * none of it means anything to a thumb. No three.js here, so the pages that are not
 * lazy can draw it beside their own controls.
 */
export default function ModelControlsHelp({ canPan = true, canPivot = true, className }: ModelControlsHelpProps) {
  const { t } = useTranslation();
  const alt = isMac() ? '⌥' : 'Alt';
  const shift = isMac() ? '⇧' : 'Shift';

  const rows: [string, string][] = [
    [t('cragModel.viewer.controls.rotateKeys'), t('cragModel.viewer.controls.rotate')],
    [t('cragModel.viewer.controls.zoomKeys'), t('cragModel.viewer.controls.zoom')],
  ];
  if (canPan) {
    rows.push(
      [`${shift} + ${t('cragModel.viewer.controls.scroll')}`, t('cragModel.viewer.controls.panHorizontal')],
      [`${alt} + ${t('cragModel.viewer.controls.scroll')}`, t('cragModel.viewer.controls.panVertical')],
      [t('cragModel.viewer.controls.panKeys', { shift }), t('cragModel.viewer.controls.pan')],
      ['← ↑ ↓ →', t('cragModel.viewer.controls.arrows', { shift })],
    );
  }
  if (canPivot) rows.push(['F', t('cragModel.viewer.controls.pivot')]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('cragModel.viewer.controls.title')}
          title={t('cragModel.viewer.controls.title')}
          className={cn('hidden h-9 w-9 [@media(hover:hover)]:inline-flex', className)}
        >
          <CircleHelp className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <p className="mb-2 text-sm font-medium">{t('cragModel.viewer.controls.title')}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
          {rows.map(([keys, action]) => (
            <div key={keys} className="contents">
              <dt className="whitespace-nowrap font-medium">{keys}</dt>
              <dd className="text-muted-foreground">{action}</dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}
